import { useEffect, useRef, useState } from 'react'
import { Alert, Box, Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import UseTranslation from '../../hooks/UseTranslation'
import useBiometricSettings from '../../hooks/useBiometricSettings'
import SettingRow from './SettingRow'
import EnableBiometricDialog from './EnableBiometricDialog'
import PromptDialog from './PromptDialog'
import { BackArrowIcon } from './icons'
import tokens, { keyframes } from './tokens'
import { isNativePlatform } from '../../services/Firebase/native'
import {
  isPushEnabled, enablePush, disablePush, openDeviceSettings,
} from '../../services/PushNotificationService'
import {
  enableBiometricLoginWithPassword,
  disableBiometricLogin,
} from '../../redux/features/userAuth/biometricThunk'
import { selectAuthUser } from '../../redux/features/userAuth/userAuthSlice'
import { userAuthResetAlertHandler } from '../../redux/features/userAuth/userAuthThunk'
import LoaderWithErrorHandler from '../../components/PageHelpers/LoaderWithErrorHandler'
import { REOPEN_MORE_OPTIONS_EVENT } from '../../components/ResponsiveAppBar/NavDrawer'
import AppRoutes from '../../services/AppRoutes'

// Per-biometry copy, whole sentences so Arabic can reorder freely.
//
// iOS brands its sensors, and users look for those names, so the row label
// follows the platform: Face ID / Touch ID there, the descriptive names on
// Android. Only the label changes - the rest of the copy reads the same.
const IOS_LABELS = { face: 'Face ID', fingerprint: 'Touch ID' }

const BIOMETRIC_COPY = {
  face: {
    label: 'Face Login',
    description: 'Sign in using face recognition.',
    deviceSetup: 'Set up face recognition on your device first',
    offTitle: 'Turn Off Face Login?',
    offBody: "This turns off face login on this device. You'll need your password to sign in next time.",
    on: 'Face Login enabled',
    off: 'Face Login turned off',
    failed: "Couldn't verify your face. Please try again.",
  },
  fingerprint: {
    label: 'Fingerprint Login',
    description: 'Sign in using your fingerprint.',
    deviceSetup: 'Set up fingerprint on your device first',
    offTitle: 'Turn Off Fingerprint Login?',
    offBody: "This turns off fingerprint login on this device. You'll need your password to sign in next time.",
    on: 'Fingerprint Login enabled',
    off: 'Fingerprint Login turned off',
    failed: "Couldn't verify your fingerprint. Please try again.",
  },
}

const TOAST_MS = 3000

// Used when the sensor reports itself unusable and there is no per-type copy to
// fall back on - so the dialog still says something rather than opening blank.
const GENERIC_FAILED = 'Biometric login is not available right now. Please try again.'

// Shown in the row while the OS is refusing biometry after failed attempts. It
// is temporary and self-clearing, so the line says how to lift it.
const LOCKED_OUT_HELPER = 'Too many attempts. Unlock your phone with your PIN, then try again'

/**
 * Device settings for the native apps.
 *
 * Native-only on purpose: both switches control this device (its push token and
 * its secure vault), so there is nothing here a desktop or mobile browser could
 * act on. Anyone reaching the route on web is sent home.
 */
const Settings = () => {
  const { t, language } = UseTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const userAuth = useSelector(selectAuthUser)

  const biometric = useBiometricSettings()
  const copy = BIOMETRIC_COPY[biometric.type]
  const rowLabel = copy && (Capacitor.getPlatform() === 'ios'
    ? IOS_LABELS[biometric.type]
    : (t?.[copy.label] || copy.label))

  // Why the row is switched off, in the amber helper line under it. A lockout
  // clears itself, so the wording says what unblocks it rather than treating it
  // as an error.
  let blockedHelper = ''
  if (copy && biometric.blockedBy === 'not-enrolled') {
    blockedHelper = t?.[copy.deviceSetup] || copy.deviceSetup
  } else if (biometric.blockedBy === 'locked-out') {
    blockedHelper = t?.[LOCKED_OUT_HELPER] || LOCKED_OUT_HELPER
  }

  const [push, setPush] = useState(false)
  const [pushBusy, setPushBusy] = useState(false)
  const [busy, setBusy] = useState(false)
  const [enableDialog, setEnableDialog] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [disableDialog, setDisableDialog] = useState(false)
  const [failedDialog, setFailedDialog] = useState(false)
  const [blockedDialog, setBlockedDialog] = useState(false)
  const [toast, setToast] = useState('')

  // Held only between a failed device prompt and its retry, so "Try Again" can
  // reopen the sensor without a second password entry. Cleared as soon as the
  // flow ends, either way.
  const lastPassword = useRef('')

  const native = isNativePlatform()
  const isArabic = language === 'AR'

  useEffect(() => {
    if (!native) {
      history.replace(AppRoutes.home)
      return
    }
    isPushEnabled().then(setPush)
  }, [native])

  useEffect(() => {
    if (!toast) return undefined
    const timer = setTimeout(() => setToast(''), TOAST_MS)
    return () => clearTimeout(timer)
  }, [toast])

  // A lockout expires on its own, and enrolling a finger or face happens in the
  // device's settings - both outside this app. Re-reading whenever the screen
  // comes back into view means the row un-blocks itself the moment the user
  // returns, instead of staying stuck until they navigate away and back.
  useEffect(() => {
    if (!native) return undefined

    const recheck = () => {
      if (document.visibilityState === 'visible') biometric.refresh()
    }

    window.addEventListener('focus', recheck)
    document.addEventListener('visibilitychange', recheck)

    return () => {
      window.removeEventListener('focus', recheck)
      document.removeEventListener('visibilitychange', recheck)
    }
  }, [native, biometric.refresh])

  // Each control owns its lock, and every path releases it in a finally: a
  // push call that fails must never leave the biometric row unresponsive.
  const togglePush = async () => {
    if (pushBusy) return
    setPushBusy(true)

    try {
      if (push) {
        // Flip first - the preference write behind this is local and instant,
        // and the token deletion it kicks off must not hold up the switch.
        setPush(false)
        await disablePush()
        setToast(t?.['Push notifications off'] || 'Push notifications off')
      } else {
        const { enabled, blocked } = await enablePush()
        setPush(enabled)
        setToast(t?.['Push notifications on'] || 'Push notifications on')

        // The preference is on either way; the popup only explains that the
        // device is still holding notifications back, and offers the way out.
        if (blocked) setBlockedDialog(true)
      }
    } catch (error) {
      // Nothing was promised to the user beyond the switch, so re-read the
      // stored preference rather than leaving it showing a guess.
      setPush(await isPushEnabled())
    } finally {
      setPushBusy(false)
    }
  }

  /**
   * The capability is re-read on every tap because it was last measured when
   * the screen mounted, and the OS can withdraw biometry underneath us - a
   * lockout after repeated failed attempts reports the sensor as unavailable.
   * Acting on the stale copy is how a tap ends up doing nothing at all.
   *
   * When it is unusable the row explains itself inline rather than throwing a
   * dialog: there is nothing to confirm, and the state clears on its own.
   */
  const toggleBiometric = async () => {
    if (busy) return
    setBusy(true)

    try {
      setPasswordError('')
      const current = await biometric.refresh()

      if (current.blockedBy || !current.type) return

      if (current.enrolled) setDisableDialog(true)
      else setEnableDialog(true)
    } finally {
      setBusy(false)
    }
  }

  const confirmEnable = async (password) => {
    setBusy(true)
    setPasswordError('')
    lastPassword.current = password

    let result
    try {
      result = await dispatch(enableBiometricLoginWithPassword({
        password,
        reason: t?.['Enable biometric login'] || 'Enable biometric login',
      }))
    } catch (error) {
      setBusy(false)
      setEnableDialog(false)
      setFailedDialog(true)
      return
    }

    setBusy(false)

    if (result.meta.requestStatus === 'fulfilled') {
      lastPassword.current = ''
      setEnableDialog(false)
      await biometric.refresh()
      setToast(t?.[copy.on] || copy.on)
      return
    }

    const payload = result.payload || {}
    if (payload.wrongPassword) {
      setPasswordError(t?.['Incorrect password'] || 'Incorrect password')
      return
    }

    // Cancelling the OS prompt is a choice, not a failure - close quietly.
    setEnableDialog(false)
    if (!payload.cancelled) setFailedDialog(true)
  }

  const confirmDisable = async () => {
    setBusy(true)
    try {
      await dispatch(disableBiometricLogin())
      setDisableDialog(false)
      await biometric.refresh()
      setToast(t?.[copy.off] || copy.off)
    } finally {
      setBusy(false)
    }
  }

  if (!native) return null

  return (
    <Box sx={styles.root}>
      <Box sx={styles.header}>
        <Box
          sx={[styles.back, isArabic && styles.backFlipped]}
          onClick={() => {
            // Land back in More Options, which is where this screen was opened
            // from, rather than on the page behind it. The page behind mounts a
            // brand new NavDrawer (see AuthRoute.js), so the flag has to survive
            // that remount - a plain event fired now would already be gone by
            // the time the new drawer's listener could exist.
            try {
              sessionStorage.setItem(REOPEN_MORE_OPTIONS_EVENT, '1')
            } catch (e) {
              // sessionStorage is unavailable (private browsing, disabled
              // storage): fall back to a live event. It only reaches a
              // NavDrawer instance still mounted when goBack() below fires,
              // so it is best-effort rather than the guaranteed path above.
              window.dispatchEvent(new Event(REOPEN_MORE_OPTIONS_EVENT))
            }
            history.goBack()
          }}
          data-testid="settingsBack"
        >
          <BackArrowIcon />
        </Box>
        <Typography sx={styles.headerTitle}>{t?.Settings || 'Settings'}</Typography>
      </Box>

      <Box sx={styles.scroll}>
        <Typography sx={styles.sectionTitle}>
          {t?.Notifications || 'Notifications'}
        </Typography>

        <Box sx={styles.section}>
          <SettingRow
            label={t?.['Push Notifications'] || 'Push Notifications'}
            description={t?.['Get alerts for approvals, requests and announcements.'] ||
              'Get alerts for approvals, requests and announcements.'}
            checked={push}
            onToggle={togglePush}
          />
        </Box>

        {biometric.supported && (
          <>
            <Typography sx={[styles.sectionTitle, styles.sectionTitleSpaced]}>
              {t?.Security || 'Security'}
            </Typography>

            <Box sx={styles.section}>
              <SettingRow
                label={rowLabel}
                description={t?.[copy.description] || copy.description}
                checked={biometric.enrolled}
                disabled={Boolean(biometric.blockedBy)}
                helperText={blockedHelper}
                onToggle={toggleBiometric}
              />
            </Box>
          </>
        )}
      </Box>

      <EnableBiometricDialog
        open={enableDialog}
        type={biometric.type}
        loading={busy}
        error={passwordError}
        onConfirm={confirmEnable}
        onCancel={() => {
          lastPassword.current = ''
          setEnableDialog(false)
        }}
      />

      <PromptDialog
        open={disableDialog}
        icon="alert"
        danger
        title={copy ? (t?.[copy.offTitle] || copy.offTitle) : ''}
        body={copy ? (t?.[copy.offBody] || copy.offBody) : ''}
        confirmText={t?.['Turn Off'] || 'Turn Off'}
        cancelText={t?.Cancel || 'Cancel'}
        loading={busy}
        onConfirm={confirmDisable}
        onCancel={() => setDisableDialog(false)}
      />

      <PromptDialog
        open={failedDialog}
        icon="alert"
        body={copy
          ? (t?.[copy.failed] || copy.failed)
          : (t?.[GENERIC_FAILED] || GENERIC_FAILED)}
        confirmText={t?.['Try Again'] || 'Try Again'}
        cancelText={t?.Cancel || 'Cancel'}
        onConfirm={() => {
          setFailedDialog(false)
          // The password was already verified before the first prompt, so this
          // goes straight back to the sensor rather than asking again.
          if (lastPassword.current) confirmEnable(lastPassword.current)
          else setEnableDialog(true)
        }}
        onCancel={() => {
          lastPassword.current = ''
          setFailedDialog(false)
        }}
      />

      <PromptDialog
        open={blockedDialog}
        icon="bell"
        title={t?.['Turn on notifications for Orgarise?'] || 'Turn on notifications for Orgarise?'}
        body={t?.['Notifications are turned off for Orgarise in your device settings. Open settings to turn them on.'] ||
          'Notifications are turned off for Orgarise in your device settings. Open settings to turn them on.'}
        confirmText={t?.['Open Settings'] || 'Open Settings'}
        cancelText={t?.['Not Now'] || 'Not Now'}
        onConfirm={async () => {
          setBlockedDialog(false)
          await openDeviceSettings()
        }}
        onCancel={() => setBlockedDialog(false)}
      />

      {toast && (
        <Box sx={styles.toast}>
          <Alert severity="success" sx={styles.alert}>{toast}</Alert>
        </Box>
      )}

      <LoaderWithErrorHandler store={userAuth} resetAlertHandler={userAuthResetAlertHandler} />
    </Box>
  )
}

const styles = {
  root: {
    ...keyframes,
    position: 'relative',
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.color.surfacePage,
    animation: `settingsFade ${tokens.duration.base} ${tokens.ease.standard}`,
  },
  header: {
    flexShrink: 0,
    height: '60px',
    display: 'flex',
    alignItems: 'center',
    gap: '18px',
    padding: '0 20px',
    backgroundColor: tokens.color.white,
    borderBottom: `1px solid ${tokens.color.border}`,
  },
  back: {
    display: 'inline-flex',
    cursor: 'pointer',
  },
  // The arrow points back, so it mirrors with the text direction.
  backFlipped: {
    transform: 'scaleX(-1)',
  },
  headerTitle: {
    fontSize: '18px',
    fontFamily: tokens.fontBold,
    color: tokens.color.textPrimary,
  },
  scroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  sectionTitle: {
    backgroundColor: tokens.color.surfaceBand,
    padding: '11px 20px',
    fontSize: '12px',
    fontFamily: tokens.fontBold,
    color: tokens.color.textDisabled,
    textTransform: 'uppercase',
  },
  sectionTitleSpaced: {
    marginTop: '24px',
  },
  section: {
    backgroundColor: tokens.color.white,
    borderBottom: `1px solid ${tokens.color.border}`,
  },
  toast: {
    position: 'fixed',
    left: '16px',
    right: '16px',
    bottom: '24px',
    zIndex: 1400,
    animation: `settingsSnack ${tokens.duration.slow} ${tokens.ease.out}`,
  },
  alert: {
    minHeight: '52px',
    alignItems: 'center',
    borderRadius: tokens.radius.alert,
    backgroundColor: tokens.color.successLight,
    color: tokens.color.greenSuccessDark,
    fontSize: '14px',
    '& .MuiAlert-icon': {
      color: tokens.color.green,
    },
  },
}

export default Settings
